/**
 * Layout da área autenticada.
 *
 * Sidebar no desktop, barra inferior no celular. A escolha de não reaproveitar
 * a mesma navegação nos dois é deliberada: uma sidebar espremida em 375 px é
 * pior que uma tab bar, e uma tab bar em 1440 px desperdiça a tela toda.
 */

import {
  Bell,
  BadgeDollarSign,
  Home,
  LogOut,
  Menu,
  Moon,
  Sun,
  Users,
  // O lucide não tem bola de futebol — `Football` é a americana. `Volleyball`
  // é a única redonda com gomos, então vira a bola do produto.
  Volleyball as Ball,
  X,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';

import { Avatar, Button, cx } from '@/components/ui';
import { useUnreadCount } from '@/lib/queries';
import { useAuth } from '@/store/auth';
import { useTheme } from '@/theme';

const NAV = [
  { to: '/', label: 'Início', icon: Home, end: true },
  { to: '/grupos', label: 'Peladas', icon: Ball },
  { to: '/perfil', label: 'Perfil', icon: Users },
] as const;

/** Só para quem administra a plataforma; fora da barra inferior do celular. */
const PLATFORM_NAV = { to: '/admin/assinaturas', label: 'Assinaturas', icon: BadgeDollarSign };

export function AppLayout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();

  const user = useAuth((state) => state.user);
  const logout = useAuth((state) => state.logout);
  const { data: unread } = useUnreadCount();

  async function handleLogout() {
    await logout();
    navigate('/entrar', { replace: true });
  }

  return (
    <div className="min-h-screen bg-bg">
      {/* ── Sidebar (desktop) ────────────────────────────────────────────── */}
      <aside className="fixed inset-y-0 left-0 hidden w-60 flex-col border-r border-line bg-surface lg:flex">
        <div className="flex h-16 items-center gap-2.5 border-b border-line px-5">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand">
            <Ball className="h-5 w-5 text-on-brand" aria-hidden />
          </span>
          <span className="text-lg font-extrabold tracking-tight">NossoRachão</span>
        </div>

        <nav className="flex-1 space-y-1 p-3">
          {NAV.map((item) => (
            <SidebarLink key={item.to} {...item} />
          ))}
          {user?.isPlatformAdmin ? <SidebarLink {...PLATFORM_NAV} /> : null}
        </nav>

        <div className="border-t border-line p-3">
          <button
            type="button"
            onClick={toggle}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-ink-2 transition-colors hover:bg-sunken"
          >
            {theme === 'dark' ? (
              <Sun className="h-4 w-4" aria-hidden />
            ) : (
              <Moon className="h-4 w-4" aria-hidden />
            )}
            {theme === 'dark' ? 'Tema claro' : 'Tema escuro'}
          </button>

          <div className="mt-2 flex items-center gap-2.5 rounded-lg px-3 py-2">
            <Avatar name={user?.name ?? '?'} src={user?.avatarUrl} size={32} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] font-semibold">{user?.name}</p>
              <p className="truncate text-[11px] text-ink-3">{user?.email}</p>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              aria-label="Sair da conta"
              className="rounded p-1.5 text-ink-3 transition-colors hover:bg-sunken hover:text-bad"
            >
              <LogOut className="h-4 w-4" aria-hidden />
            </button>
          </div>
        </div>
      </aside>

      {/* ── Topo (mobile) ────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-line bg-surface px-4 lg:hidden">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-brand">
            <Ball className="h-4 w-4 text-on-brand" aria-hidden />
          </span>
          <span className="font-extrabold">NossoRachão</span>
        </div>

        <div className="flex items-center gap-1">
          <IconButton onClick={() => navigate('/notificacoes')} label="Notificações">
            <span className="relative">
              <Bell className="h-5 w-5" aria-hidden />
              {unread && unread.count > 0 ? (
                <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-bad" />
              ) : null}
            </span>
          </IconButton>

          <IconButton onClick={() => setMenuOpen(true)} label="Abrir menu">
            <Menu className="h-5 w-5" aria-hidden />
          </IconButton>
        </div>
      </header>

      {/* ── Menu lateral (mobile) ────────────────────────────────────────── */}
      {menuOpen ? (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            aria-label="Fechar menu"
            className="absolute inset-0 bg-black/50"
            onClick={() => setMenuOpen(false)}
          />

          <div className="absolute right-0 top-0 flex h-full w-72 animate-fade-in flex-col bg-surface">
            <div className="flex h-14 items-center justify-between border-b border-line px-4">
              <span className="font-bold">Menu</span>
              <IconButton onClick={() => setMenuOpen(false)} label="Fechar">
                <X className="h-5 w-5" aria-hidden />
              </IconButton>
            </div>

            <nav className="flex-1 space-y-1 p-3" onClick={() => setMenuOpen(false)}>
              {NAV.map((item) => (
                <SidebarLink key={item.to} {...item} />
              ))}
              {user?.isPlatformAdmin ? <SidebarLink {...PLATFORM_NAV} /> : null}
            </nav>

            <div className="space-y-1 border-t border-line p-3">
              <button
                type="button"
                onClick={toggle}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-ink-2 hover:bg-sunken"
              >
                {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                {theme === 'dark' ? 'Tema claro' : 'Tema escuro'}
              </button>

              <button
                type="button"
                onClick={handleLogout}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-bad hover:bg-sunken"
              >
                <LogOut className="h-4 w-4" aria-hidden />
                Sair da conta
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* ── Conteúdo ─────────────────────────────────────────────────────── */}
      <main className="pb-[calc(5rem+env(safe-area-inset-bottom))] lg:pb-0 lg:pl-60">
        <div className="w-full animate-fade-in p-4 lg:p-8">
          <Outlet />
        </div>
      </main>

      {/* ── Barra inferior (mobile) ──────────────────────────────────────── */}
      <nav className="fixed inset-x-0 bottom-0 z-30 flex border-t border-line bg-surface pb-[env(safe-area-inset-bottom)] lg:hidden">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={'end' in item ? item.end : undefined}
            className={({ isActive }) =>
              cx(
                'flex flex-1 flex-col items-center gap-0.5 py-2.5 text-[10px] font-semibold',
                isActive ? 'text-brand' : 'text-ink-3',
              )
            }
          >
            <item.icon className="h-5 w-5" aria-hidden />
            {item.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

function SidebarLink({
  to,
  label,
  icon: Icon,
  end,
}: {
  to: string;
  label: string;
  icon: typeof Home;
  end?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cx(
          'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-semibold transition-colors',
          isActive ? 'bg-brand-soft text-brand' : 'text-ink-2 hover:bg-sunken',
        )
      }
    >
      <Icon className="h-4 w-4" aria-hidden />
      {label}
    </NavLink>
  );
}

function IconButton({
  children,
  onClick,
  label,
}: {
  children: ReactNode;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="rounded-lg p-2 text-ink-2 transition-colors hover:bg-sunken"
    >
      {children}
    </button>
  );
}

/** Cabeçalho de página, com ação opcional à direita. */
export function PageHeader({
  title,
  subtitle,
  action,
  back,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
  back?: { to: string; label: string };
}) {
  const navigate = useNavigate();

  return (
    <div className="mb-6">
      {back ? (
        <Button variant="ghost" size="sm" className="mb-2 -ml-3" onClick={() => navigate(back.to)}>
          ← {back.label}
        </Button>
      ) : null}

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight">{title}</h1>
          {subtitle ? <p className="mt-0.5 text-sm text-ink-2">{subtitle}</p> : null}
        </div>
        {action}
      </div>
    </div>
  );
}
